import React, { createRef } from 'react';

// styling
import './Customer.css';
import './CustomerProfile.css';

// constants + icons
import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import {
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { Typography } from '@mui/material';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {
  ROLE_ADMIN,
  ROLE_OWNER,
  ROLE_MASTER_ADMIN,
  ROLE_MANAGER,
  ROLE_TECH_ADMIN,
} from '../global/constants';
import { BASE_URL, CUSTOMER } from '../global/Server/endpoints';

const POINTS_EDIT_ROLES = [
  ROLE_ADMIN,
  ROLE_OWNER,
  ROLE_MASTER_ADMIN,
  ROLE_MANAGER,
  ROLE_TECH_ADMIN,
];

const ROLE_WALLET = 'ROLE_CUSTOMER_WALLET_PASS';

const TAB_INFO = 'info';
const TAB_POINTS = 'points';
const TAB_RULES = 'rules';
const TAB_WALLET = 'wallet';

const TABS = [
  { id: TAB_INFO, label: 'Info' },
  { id: TAB_POINTS, label: 'Points' },
  { id: TAB_RULES, label: 'Match Rules' },
  { id: TAB_WALLET, label: 'Wallet' },
];

const STATUS_ACTIVE = 'Active';
const STATUS_UNACTIVE = 'Unactive';
const STATUS_BANNED = 'Banned';

// Format a date/string safely for date inputs
const toDateOrNull = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// Format a stored dob for read-only display
const formatDisplayDate = (v) => {
  if (!v) return '—';
  const d = toDateOrNull(v);
  if (!d) return String(v);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const formatPhone = (v) => {
  if (!v) return '—';
  const digits = String(v).replace(/\D/g, '');
  if (digits.length !== 10) return v;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const initialsFor = (customer) => {
  const f = (customer?.firstName || '').trim().charAt(0).toUpperCase();
  const l = (customer?.lastName || '').trim().charAt(0).toUpperCase();
  return `${f}${l}` || '?';
};

// Map persisted flags (isBlocked, isInactive) to the three-state pill.
// Banned takes precedence over Unactive if both flags happen to be true.
const statusFromCustomer = (customer) => {
  if (!customer) return STATUS_ACTIVE;
  if (customer.isBlocked === true) return STATUS_BANNED;
  if (customer.isInactive === true) return STATUS_UNACTIVE;
  return STATUS_ACTIVE;
};

class CustomerProfile extends React.Component {
  constructor(props) {
    super(props);
    const c = props.customer || {};
    this.state = {
      activeTab: TAB_INFO,

      // Info editing
      infoEditing: false,
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: c.phone || '',
      dob: toDateOrNull(c.dob),
      comments: c.comments || '',

      // Points
      pointsEditing: false,
      points:
        typeof c.points === 'number' ? c.points : parseInt(c.points, 10) || 0,
      statusDraft: statusFromCustomer(c),

      // Match rules
      isRule: !!c.isRule,
      timeLimit: c.timeLimit != null ? c.timeLimit : '',
      maxMatchesPerDay: c.maxMatchesPerDay != null ? c.maxMatchesPerDay : '',
      maxAmountPerDay: c.maxAmountPerDay != null ? c.maxAmountPerDay : '',
      matchValues: Array.isArray(c.matchValues) ? [...c.matchValues] : [],
      rulesDirty: false,

      // Wallet
      walletType: 'apple',
      walletQrUrl: null,
      walletPassUrl: null,
      walletDialogOpen: false,
      isGeneratingWallet: false,
      walletChecked: false,

      // Photo retake (Info tab edit mode)
      showVideo: false,
      cameraFacingMode: 'environment',
      capturedImageURL: null,
      newPhotoFile: null,

      // Photo zoom dialog
      photoZoomOpen: false,

      // Status menu (header dropdown)
      statusMenuAnchor: null,

      // Recent visits (last 10) — lazy-loaded on first Points tab view
      recentVisits: [],
      recentVisitsLoaded: false,
      recentVisitsLoading: false,
    };
    this.videoRef = createRef();
    this.canvasRef = createRef();
  }

  componentWillUnmount() {
    this.stopCamera();
  }

  componentDidMount() {
    if (this.hasWalletRole() && this.props.customer?.customerId) {
      this.fetchExistingWalletPass();
    }
  }

  componentDidUpdate(prevProps) {
    // If the profile customer reference changes (e.g., save returned a
    // fresher copy from Redux), rehydrate readable fields.
    if (prevProps.customer !== this.props.customer && this.props.customer) {
      const c = this.props.customer;
      this.setState((prev) => ({
        firstName: prev.infoEditing ? prev.firstName : c.firstName || '',
        lastName: prev.infoEditing ? prev.lastName : c.lastName || '',
        phone: prev.infoEditing ? prev.phone : c.phone || '',
        dob: prev.infoEditing ? prev.dob : toDateOrNull(c.dob),
        comments: prev.infoEditing ? prev.comments : c.comments || '',
        points: prev.pointsEditing
          ? prev.points
          : typeof c.points === 'number'
          ? c.points
          : parseInt(c.points, 10) || 0,
        statusDraft: prev.pointsEditing
          ? prev.statusDraft
          : statusFromCustomer(c),
        isRule: prev.rulesDirty ? prev.isRule : !!c.isRule,
        timeLimit: prev.rulesDirty
          ? prev.timeLimit
          : c.timeLimit != null
          ? c.timeLimit
          : '',
        maxMatchesPerDay: prev.rulesDirty
          ? prev.maxMatchesPerDay
          : c.maxMatchesPerDay != null
          ? c.maxMatchesPerDay
          : '',
        maxAmountPerDay: prev.rulesDirty
          ? prev.maxAmountPerDay
          : c.maxAmountPerDay != null
          ? c.maxAmountPerDay
          : '',
        matchValues: prev.rulesDirty
          ? prev.matchValues
          : Array.isArray(c.matchValues)
          ? [...c.matchValues]
          : [],
      }));
    }
  }

  hasWalletRole = () => {
    const roles = this.props.user?.authority || [];
    return roles.includes(ROLE_WALLET);
  };

  isAdmin = () => {
    const roles = this.props.user?.authority || [];
    return roles.includes(ROLE_ADMIN);
  };

  canEditPoints = () => {
    const roles = this.props.user?.authority || [];
    return POINTS_EDIT_ROLES.some((r) => roles.includes(r));
  };

  // ── Camera (photo retake in Info edit) ──
  openCamera = () => {
    this.setState(
      { showVideo: true, capturedImageURL: null, newPhotoFile: null },
      () => this.startWebcam()
    );
  };

  startWebcam = async () => {
    if (!this.videoRef.current) return;
    try {
      const existing = this.videoRef.current?.srcObject;
      if (existing) {
        existing.getTracks().forEach((track) => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: this.state.cameraFacingMode } },
      });
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        this.videoRef.current.play();
      }
    } catch (err) {
      this.props.openFeedbackBar('error', 'Camera not available');
      this.setState({ showVideo: false });
    }
  };

  switchCamera = () => {
    const nextFacingMode =
      this.state.cameraFacingMode === 'environment' ? 'user' : 'environment';
    this.setState({ cameraFacingMode: nextFacingMode }, () => {
      if (this.state.showVideo) {
        this.startWebcam();
      }
    });
  };

  stopCamera = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    if (this.videoRef.current) this.videoRef.current.srcObject = null;
    this.setState({ showVideo: false });
  };

  captureImage = () => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (!canvas || !video) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageURL = canvas.toDataURL('image/jpeg', 0.5);
    this.setState({ capturedImageURL: imageURL }, () => this.stopCamera());
    this.addCapturedPhotoToState(imageURL);
  };

  addCapturedPhotoToState = (fileDataURL) => {
    fetch(fileDataURL)
      .then((res) => res.blob())
      .then(async (blob) => {
        const file = new File(
          [blob],
          `${Date.now() + Math.floor(Math.random() * 10000)}.jpg`,
          { type: 'image/jpeg' }
        );
        const newPhotoFile = {
          localURL: URL.createObjectURL(file),
          displayName: file.name,
          type: file.type,
          file,
        };
        this.setState({
          newPhotoFile,
          capturedImageURL: newPhotoFile.localURL,
        });
      });
  };

  // ── Shared save helper ──
  submitCustomerUpdate = (overrides, successMsg) => {
    const c = this.props.customer || {};
    const payload = {
      locationId: c.locationId || this.props.user?.selectedLocationId || '',
      customerId: c.customerId,
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      dob: this.state.dob,
      points: parseInt(this.state.points, 10) || 0,
      driverLicense: c.driverLicense || '',
      phone: this.state.phone,
      address: c.address || '',
      comments: this.state.comments,
      files: this.state.newPhotoFile
        ? [this.state.newPhotoFile]
        : c.files || [],
      newFiles: this.state.newPhotoFile ? [this.state.newPhotoFile] : [],
      isRule: this.state.isRule,
      maxMatchesPerDay:
        this.state.maxMatchesPerDay === '' ? null : this.state.maxMatchesPerDay,
      timeLimit: this.state.timeLimit === '' ? null : this.state.timeLimit,
      maxAmountPerDay:
        this.state.maxAmountPerDay === '' ? null : this.state.maxAmountPerDay,
      isBlocked: this.state.statusDraft === STATUS_BANNED,
      isInactive: this.state.statusDraft === STATUS_UNACTIVE,
      matchValues: (this.state.matchValues || []).filter((v) => v !== ''),
      userId: this.props.user?.userId,
      employeeName: [this.props.user?.firstname, this.props.user?.lastname]
        .filter((p) => p && p !== 'null' && p !== 'undefined')
        .join(' '),
      capturedImageURL:
        this.state.capturedImageURL || c.capturedImageURL || null,
      ...overrides,
    };

    this.props.saveCustomer(
      payload,
      () => {
        this.props.openFeedbackBar('success', successMsg || 'Saved');
      },
      (message) => {
        this.props.openFeedbackBar('error', message);
      }
    );
  };

  // ── Tabs ─────────────────────────────────────────────────────────────────
  setTab = (tabId) => {
    this.setState({ activeTab: tabId });
    if (tabId === TAB_POINTS && !this.state.recentVisitsLoaded) {
      this.fetchRecentVisits();
    }
  };

  fetchRecentVisits = async () => {
    const customerId = this.props.customer?.customerId;
    if (!customerId) return;

    this.setState({ recentVisitsLoading: true });
    try {
      const response = await fetch(CUSTOMER.GET_RECENT_VISITS, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.success) {
          this.setState({
            recentVisits: Array.isArray(data.recentVisits)
              ? data.recentVisits
              : [],
          });
        }
      }
    } catch (err) {
      // silent — visits panel will show its empty state
    } finally {
      this.setState({
        recentVisitsLoading: false,
        recentVisitsLoaded: true,
      });
    }
  };

  // ── Info tab ─────────────────────────────────────────────────────────────
  beginInfoEdit = () => this.setState({ infoEditing: true });

  cancelInfoEdit = () => {
    const c = this.props.customer || {};
    this.stopCamera();
    this.setState({
      infoEditing: false,
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: c.phone || '',
      dob: toDateOrNull(c.dob),
      comments: c.comments || '',
      capturedImageURL: null,
      newPhotoFile: null,
      showVideo: false,
    });
  };

  saveInfo = () => {
    this.submitCustomerUpdate({}, 'Info updated');
    this.stopCamera();
    this.setState({
      infoEditing: false,
      capturedImageURL: null,
      newPhotoFile: null,
      showVideo: false,
    });
  };

  handlePhoneInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    this.setState({ phone: digits });
  };

  // ── Status dropdown (header) ─────────────────────────────────────────────
  openStatusMenu = (e) => {
    this.setState({ statusMenuAnchor: e.currentTarget });
  };

  closeStatusMenu = () => {
    this.setState({ statusMenuAnchor: null });
  };

  handleStatusChange = (newStatus) => {
    this.setState({ statusDraft: newStatus, statusMenuAnchor: null });
    if (newStatus === statusFromCustomer(this.props.customer)) return;
    this.submitCustomerUpdate(
      {
        isBlocked: newStatus === STATUS_BANNED,
        isInactive: newStatus === STATUS_UNACTIVE,
      },
      'Status updated'
    );
  };

  // ── Points tab ───────────────────────────────────────────────────────────
  beginPointsEdit = () => {
    if (!this.canEditPoints()) return;
    this.setState({ pointsEditing: true });
  };

  cancelPointsEdit = () => {
    const c = this.props.customer || {};
    this.setState({
      pointsEditing: false,
      points:
        typeof c.points === 'number' ? c.points : parseInt(c.points, 10) || 0,
      statusDraft: statusFromCustomer(c),
    });
  };

  savePoints = () => {
    this.submitCustomerUpdate({}, 'Points updated');
    this.setState({ pointsEditing: false });
  };

  adjustPoints = (delta) => {
    const next = Math.max(0, (parseInt(this.state.points, 10) || 0) + delta);
    this.setState({ points: next });
  };

  // ── Rules tab ────────────────────────────────────────────────────────────
  toggleRule = () => {
    if (!this.isAdmin()) return;
    this.setState((prev) => ({ isRule: !prev.isRule, rulesDirty: true }));
  };

  handleRuleField = (e) => {
    const { name, value } = e.target;
    const next = { [name]: value, rulesDirty: true };

    // Auto-split the Daily Match Cap into two equal match amounts so an
    // admin entering "100" gets [50, 50] without having to type the pair
    // by hand. They can still edit either amount or + Add Match Amount
    // afterwards.
    if (name === 'maxAmountPerDay') {
      const cap = Number(value);
      if (!Number.isNaN(cap) && cap > 0) {
        const half = cap / 2;
        next.matchValues = [String(half), String(half)];
      } else if (value === '' || value === null) {
        next.matchValues = [];
      }
    }

    this.setState(next);
  };

  handleMatchValueChange = (idx, value) => {
    const updated = [...this.state.matchValues];
    updated[idx] = value;
    this.setState({ matchValues: updated, rulesDirty: true });
  };

  addMatchValue = () => {
    this.setState((prev) => ({
      matchValues: [...prev.matchValues, ''],
      rulesDirty: true,
    }));
  };

  removeMatchValue = (idx) => {
    this.setState((prev) => ({
      matchValues: prev.matchValues.filter((_, i) => i !== idx),
      rulesDirty: true,
    }));
  };

  saveRules = () => {
    this.submitCustomerUpdate({}, 'Match rules updated');
    this.setState({ rulesDirty: false });
  };

  // ── Wallet tab ───────────────────────────────────────────────────────────
  fetchExistingWalletPass = async () => {
    const customerId = this.props.customer?.customerId;
    if (!customerId) return;

    try {
      this.setState({ isGeneratingWallet: true });
      const response = await fetch(
        `${BASE_URL}/wallet/existing/${customerId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.qrUrl) {
          this.setState({
            walletQrUrl: data.qrUrl,
            walletPassUrl: data.passUrl,
            walletType: data.walletType || 'apple',
          });
        }
      }
    } catch (err) {
      // silent
    } finally {
      this.setState({ isGeneratingWallet: false, walletChecked: true });
    }
  };

  generateWalletPass = async () => {
    const customerId = this.props.customer?.customerId;
    if (!customerId) return;

    try {
      this.setState({ isGeneratingWallet: true });

      const payload = {
        customerId,
        phone: this.state.phone || this.props.customer?.phone,
        walletType: this.state.walletType,
        locationId: this.props.user?.selectedLocationId,
      };

      const response = await fetch(`${BASE_URL}/wallet/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        this.props.openFeedbackBar('error', 'Failed to generate wallet pass');
        return;
      }

      const data = await response.json();
      this.setState({
        walletQrUrl: data.qrUrl,
        walletPassUrl: data.passUrl,
        walletDialogOpen: true,
      });
    } catch (err) {
      this.props.openFeedbackBar('error', err.message);
    } finally {
      this.setState({ isGeneratingWallet: false });
    }
  };

  openWalletDialog = () => this.setState({ walletDialogOpen: true });
  closeWalletDialog = () => this.setState({ walletDialogOpen: false });

  openPhotoZoom = () => this.setState({ photoZoomOpen: true });
  closePhotoZoom = () => this.setState({ photoZoomOpen: false });

  // ── Rendering ────────────────────────────────────────────────────────────
  renderHeader = () => {
    const c = this.props.customer || {};
    const status = statusFromCustomer(c);

    let statusClass = 'customer-status-active';
    if (status === STATUS_BANNED) statusClass = 'customer-status-banned';
    else if (status === STATUS_UNACTIVE)
      statusClass = 'customer-status-unactive';

    const fullName =
      `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Customer';

    const avatarUrl =
      this.state.capturedImageURL ||
      c.capturedImageURL ||
      (c.files && c.files[0] && c.files[0].localURL) ||
      null;

    return (
      <div className="customer-profile-header">
        <div
          className="customer-avatar"
          onClick={avatarUrl ? this.openPhotoZoom : undefined}
          style={avatarUrl ? { cursor: 'zoom-in' } : undefined}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={fullName}
              className="customer-avatar-img"
            />
          ) : (
            initialsFor(c)
          )}
        </div>
        <div className="customer-header-name-block">
          <div className="customer-header-name">{fullName}</div>
          <div className="customer-header-phone">{formatPhone(c.phone)}</div>
        </div>
        <button
          type="button"
          className={`customer-status-pill customer-status-pill-button ${statusClass} ${
            Boolean(this.state.statusMenuAnchor)
              ? 'customer-status-pill-button-open'
              : ''
          }`}
          onClick={this.openStatusMenu}
          aria-label="Change customer status"
        >
          <span className="customer-status-dot" />
          {status}
          <ArrowDropDownIcon
            className={`customer-status-pill-chevron ${
              Boolean(this.state.statusMenuAnchor)
                ? 'customer-status-pill-chevron-open'
                : ''
            }`}
          />
        </button>
        <Menu
          anchorEl={this.state.statusMenuAnchor}
          open={Boolean(this.state.statusMenuAnchor)}
          onClose={this.closeStatusMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PopoverClasses={{ paper: 'customer-status-menu-paper' }}
          MenuListProps={{ className: 'customer-status-menu-list' }}
          transitionDuration={180}
        >
          {[
            { value: STATUS_ACTIVE, dotClass: 'customer-status-dot-active' },
            {
              value: STATUS_UNACTIVE,
              dotClass: 'customer-status-dot-unactive',
            },
            { value: STATUS_BANNED, dotClass: 'customer-status-dot-banned' },
          ].map(({ value, dotClass }) => {
            const isSelected = value === status;
            return (
              <MenuItem
                key={value}
                selected={isSelected}
                onClick={() => this.handleStatusChange(value)}
                className="customer-status-menu-item"
                disableRipple
              >
                <span className={`customer-status-menu-dot ${dotClass}`} />
                <span className="customer-status-menu-label">{value}</span>
                {isSelected && (
                  <span className="customer-status-menu-check">✓</span>
                )}
              </MenuItem>
            );
          })}
        </Menu>
      </div>
    );
  };

  renderTabBar = () => {
    const { activeTab } = this.state;
    return (
      <div className="customer-tab-bar">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`customer-tab-btn ${
              activeTab === t.id ? 'customer-tab-btn-active' : ''
            }`}
            onClick={() => this.setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
    );
  };

  renderRow = (label, value, last) => (
    <div className={`customer-row ${last ? 'customer-row-last' : ''}`}>
      <div className="customer-row-label">{label}</div>
      <div className="customer-row-value">{value || '—'}</div>
    </div>
  );

  renderInfoTab = () => {
    const c = this.props.customer || {};
    const { infoEditing } = this.state;

    if (!infoEditing) {
      return (
        <div className="customer-card">
          {this.renderRow('First Name', c.firstName)}
          {this.renderRow('Last Name', c.lastName)}
          {this.renderRow('Phone', formatPhone(c.phone))}
          {this.renderRow('Date of Birth', formatDisplayDate(c.dob))}
          {this.renderRow('Comments', c.comments, true)}

          <Button
            variant="outlined"
            className="customer-outlined-btn"
            onClick={this.beginInfoEdit}
            fullWidth
          >
            Edit Info
          </Button>
        </div>
      );
    }

    const currentPhotoUrl =
      this.state.capturedImageURL ||
      c.capturedImageURL ||
      (c.files && c.files[0] && c.files[0].localURL) ||
      null;

    return (
      <div className="customer-card">
        <div className="customer-photo-section">
          {this.state.showVideo ? (
            <video
              ref={this.videoRef}
              className="customer-photo-video"
              autoPlay
              playsInline
              muted
            />
          ) : (
            <div
              className="customer-photo-circle"
              onClick={currentPhotoUrl ? this.openPhotoZoom : undefined}
              style={currentPhotoUrl ? { cursor: 'zoom-in' } : undefined}
            >
              {currentPhotoUrl ? (
                <img
                  src={currentPhotoUrl}
                  alt="Customer"
                  className="customer-photo-img"
                />
              ) : (
                <span className="customer-photo-placeholder">No Photo</span>
              )}
            </div>
          )}
          <canvas ref={this.canvasRef} style={{ display: 'none' }} />
          <div className="customer-photo-actions">
            {this.state.showVideo ? (
              <>
                <Button
                  className="customer-pill-btn"
                  onClick={this.captureImage}
                >
                  Capture
                </Button>
                <Button
                  className="customer-outlined-btn"
                  onClick={this.switchCamera}
                  startIcon={<FlipCameraIosIcon />}
                  style={{ marginLeft: 8 }}
                >
                  {this.state.cameraFacingMode === 'environment'
                    ? 'Front'
                    : 'Back'}
                </Button>
                <Button
                  className="customer-outlined-btn"
                  onClick={this.stopCamera}
                  style={{ marginLeft: 8 }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button className="customer-pill-btn" onClick={this.openCamera}>
                {currentPhotoUrl ? 'Retake Photo' : 'Open Camera'}
              </Button>
            )}
          </div>
        </div>

        <TextField
          className="customer-profile-input"
          variant="outlined"
          label="First Name"
          InputLabelProps={{ shrink: true }}
          value={this.state.firstName}
          onChange={(e) => this.setState({ firstName: e.target.value })}
          fullWidth
        />
        <TextField
          className="customer-profile-input"
          variant="outlined"
          label="Last Name"
          InputLabelProps={{ shrink: true }}
          value={this.state.lastName}
          onChange={(e) => this.setState({ lastName: e.target.value })}
          fullWidth
        />
        <TextField
          className="customer-profile-input"
          variant="outlined"
          label="Phone"
          InputLabelProps={{ shrink: true }}
          value={this.state.phone}
          onChange={this.handlePhoneInput}
          fullWidth
        />
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            inputVariant="outlined"
            label="Date Of Birth"
            className="customer-profile-input"
            format="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
            value={this.state.dob}
            InputAdornmentProps={{ position: 'start' }}
            onChange={(dateInput) => {
              const date = new Date(dateInput?.toString());
              if (date.toString() === 'Invalid Date') return;
              this.setState({ dob: date });
            }}
            fullWidth
          />
        </MuiPickersUtilsProvider>
        <TextField
          className="customer-profile-input"
          variant="outlined"
          label="Comments"
          InputLabelProps={{ shrink: true }}
          value={this.state.comments}
          onChange={(e) => this.setState({ comments: e.target.value })}
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
        />
        <div className="customer-btn-row">
          <Button
            variant="outlined"
            className="customer-outlined-btn"
            onClick={this.cancelInfoEdit}
          >
            Cancel
          </Button>
          <Button className="customer-primary-btn" onClick={this.saveInfo}>
            Save
          </Button>
        </div>
      </div>
    );
  };

  renderPointsTab = () => {
    const c = this.props.customer || {};
    const { pointsEditing, points } = this.state;
    const canEdit = this.canEditPoints();

    return (
      <React.Fragment>
        <div className="customer-points-card">
          <div className="customer-points-label">CURRENT POINTS</div>
          {pointsEditing && canEdit ? (
            <div className="customer-points-editor">
              <Button
                className="customer-points-adjust"
                onClick={() => this.adjustPoints(-1)}
              >
                −
              </Button>
              <input
                type="number"
                className="customer-points-input"
                value={points}
                onFocus={() => {
                  if (this.state.points === 0) {
                    this.setState({ points: '' });
                  }
                }}
                onBlur={() => {
                  if (this.state.points === '' || this.state.points === null) {
                    this.setState({ points: 0 });
                  }
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    this.setState({ points: '' });
                  } else {
                    const n = parseInt(v, 10);
                    this.setState({ points: isNaN(n) ? '' : n });
                  }
                }}
              />
              <Button
                className="customer-points-adjust"
                onClick={() => this.adjustPoints(1)}
              >
                +
              </Button>
            </div>
          ) : (
            <div
              className="customer-points-value"
              onClick={canEdit ? this.beginPointsEdit : undefined}
              role={canEdit ? 'button' : undefined}
              tabIndex={canEdit ? 0 : undefined}
              style={canEdit ? undefined : { cursor: 'default' }}
            >
              {points}
            </div>
          )}
        </div>

        <div className="customer-card">
          <div className="customer-row">
            <div className="customer-row-label">Registered By</div>
            <div className="customer-row-value">
              {(c.employeeName || '')
                .split(/\s+/)
                .filter((p) => p && p !== 'null' && p !== 'undefined')
                .join(' ') || '—'}
            </div>
          </div>
          <div className="customer-row customer-row-last">
            <div className="customer-row-label">Registered Date</div>
            <div className="customer-row-value">
              {(() => {
                const regDate = c.registrationDate;
                if (!regDate) return '—';
                const isoMatch = String(regDate).match(
                  /^(\d{4})-(\d{2})-(\d{2})(.*)/
                );
                if (isoMatch) {
                  const [, year, month, day, rest] = isoMatch;
                  return `${month}/${day}/${year}${rest}`;
                }
                return regDate;
              })()}
            </div>
          </div>
        </div>

        {pointsEditing && canEdit && (
          <div className="customer-btn-row">
            <Button
              variant="outlined"
              className="customer-outlined-btn"
              onClick={this.cancelPointsEdit}
            >
              Cancel
            </Button>
            <Button className="customer-primary-btn" onClick={this.savePoints}>
              Save
            </Button>
          </div>
        )}

        {this.renderRecentVisits()}
      </React.Fragment>
    );
  };

  // Last visit string from the server is "MM/dd/yyyy hh:mm a"; split into the
  // calendar date (Mon DD, YYYY) and the time of day for a two-line row.
  parseVisit = (visitStr) => {
    if (!visitStr) return { dateLabel: '—', timeLabel: '' };
    const d = new Date(visitStr);
    if (isNaN(d.getTime())) {
      const parts = String(visitStr).split(' ');
      return {
        dateLabel: parts[0] || visitStr,
        timeLabel: parts.slice(1).join(' '),
      };
    }
    const dateLabel = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeLabel = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return { dateLabel, timeLabel };
  };

  renderRecentVisits = () => {
    const { recentVisits, recentVisitsLoading, recentVisitsLoaded } =
      this.state;

    return (
      <div className="customer-card">
        <div className="customer-visits-header">
          <div className="customer-card-title">Recent Visits</div>
          <div className="customer-visits-badge">Last 10</div>
        </div>

        {recentVisitsLoading && (
          <div className="customer-visits-empty">Loading visits…</div>
        )}

        {!recentVisitsLoading &&
          recentVisitsLoaded &&
          recentVisits.length === 0 && (
            <div className="customer-visits-empty">No visits recorded yet.</div>
          )}

        {!recentVisitsLoading && recentVisits.length > 0 && (
          <ul className="customer-visits-list">
            {recentVisits.map((v, i) => {
              const { dateLabel, timeLabel } = this.parseVisit(v.lastVisit);
              const isFirst = i === 0;
              return (
                <li
                  key={`visit-${i}-${v.lastVisit}`}
                  className="customer-visit-row"
                >
                  <div className="customer-visit-marker">
                    <span
                      className={`customer-visit-dot ${
                        isFirst ? 'customer-visit-dot-active' : ''
                      }`}
                    />
                    {i < recentVisits.length - 1 && (
                      <span className="customer-visit-line" />
                    )}
                  </div>
                  <div className="customer-visit-body">
                    <div className="customer-visit-date">{dateLabel}</div>
                    {timeLabel && (
                      <div className="customer-visit-time">{timeLabel}</div>
                    )}
                  </div>
                  <div className="customer-visit-points">
                    <span className="customer-visit-points-value">
                      {v.points}
                    </span>
                    <span className="customer-visit-points-label">pts</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  renderRulesTab = () => {
    const {
      isRule,
      timeLimit,
      maxMatchesPerDay,
      maxAmountPerDay,
      matchValues,
      rulesDirty,
    } = this.state;

    const adminOk = this.isAdmin();

    const capNum = Number(maxAmountPerDay);
    const total = matchValues.reduce((acc, v) => acc + (Number(v) || 0), 0);
    const hasCap = !Number.isNaN(capNum) && capNum > 0;
    const overCap = hasCap && total > capNum;

    return (
      <React.Fragment>
        <div className="customer-card">
          <div className="customer-toggle-row">
            <div className="customer-toggle-info">
              <div className="customer-toggle-title">Privileged Match Rule</div>
              <div className="customer-toggle-desc">
                When enabled, this customer is subject to custom match limits
                rather than global rules.
              </div>
            </div>
            <button
              type="button"
              className={`customer-toggle ${
                isRule ? 'customer-toggle-on' : 'customer-toggle-off'
              }`}
              onClick={this.toggleRule}
              disabled={!adminOk}
            >
              <span className="customer-toggle-knob" />
            </button>
          </div>

          {isRule && (
            <div className="customer-rule-fields">
              <TextField
                className="customer-profile-input"
                variant="outlined"
                label="Wait Time (hours)"
                name="timeLimit"
                type="number"
                value={timeLimit}
                onChange={this.handleRuleField}
                InputLabelProps={{ shrink: true }}
                disabled={!adminOk}
                fullWidth
              />
              <TextField
                className="customer-profile-input"
                variant="outlined"
                label="Daily Match Limit"
                name="maxMatchesPerDay"
                type="number"
                value={maxMatchesPerDay}
                onChange={this.handleRuleField}
                InputLabelProps={{ shrink: true }}
                disabled={!adminOk}
                fullWidth
              />
              <TextField
                className="customer-profile-input"
                variant="outlined"
                label="Daily Match Cap (7am–7am)"
                name="maxAmountPerDay"
                type="number"
                value={maxAmountPerDay}
                onChange={this.handleRuleField}
                InputLabelProps={{ shrink: true }}
                disabled={!adminOk}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                helperText="max total per day"
              />
            </div>
          )}
        </div>

        {isRule && (
          <div className="customer-card">
            <div className="customer-card-title">Match Amounts</div>

            {matchValues.map((val, i) => (
              <div className="customer-match-row" key={`match-${i}`}>
                <div className="customer-match-index">{i + 1}</div>
                <TextField
                  className="customer-profile-input customer-match-input"
                  variant="outlined"
                  type="number"
                  value={val}
                  onChange={(e) =>
                    this.handleMatchValueChange(i, e.target.value)
                  }
                  disabled={!adminOk}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
                <button
                  type="button"
                  className="customer-match-delete"
                  onClick={() => this.removeMatchValue(i)}
                  disabled={!adminOk}
                  aria-label="Delete match amount"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}

            {adminOk && (
              <button
                type="button"
                className="customer-add-match-btn"
                onClick={this.addMatchValue}
              >
                + Add Match Amount
              </button>
            )}

            <div
              className={`customer-cap-indicator ${
                overCap
                  ? 'customer-cap-indicator-bad'
                  : 'customer-cap-indicator-good'
              }`}
            >
              {overCap ? (
                <WarningIcon className="customer-cap-icon" fontSize="small" />
              ) : (
                <CheckCircleIcon
                  className="customer-cap-icon"
                  fontSize="small"
                />
              )}
              <span>
                {overCap
                  ? `Exceeds cap by $${(total - capNum).toFixed(
                      2
                    )} (total $${total.toFixed(2)})`
                  : hasCap
                  ? `Total $${total.toFixed(2)} of $${capNum.toFixed(2)} cap`
                  : `Total $${total.toFixed(2)}`}
              </span>
            </div>
          </div>
        )}

        {adminOk && rulesDirty && (
          <div className="customer-btn-row">
            <Button className="customer-primary-btn" onClick={this.saveRules}>
              Save Match Rules
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  };

  renderWalletTab = () => {
    if (!this.hasWalletRole()) {
      return (
        <div className="customer-card">
          <div className="customer-info-banner customer-info-banner-yellow">
            <InfoOutlinedIcon className="customer-info-banner-icon" />
            <span>Your account does not have access to wallet passes.</span>
          </div>
        </div>
      );
    }

    const { walletType, walletQrUrl, isGeneratingWallet, walletChecked } =
      this.state;

    return (
      <div className="customer-card">
        <div className="customer-wallet-type-group">
          <div className="customer-row-label">Wallet Type</div>
          <div className="customer-wallet-type-choices">
            {[
              { id: 'apple', label: 'Apple Wallet' },
              { id: 'google', label: 'Google Wallet' },
            ].map((w) => (
              <button
                key={w.id}
                type="button"
                className={`customer-wallet-choice ${
                  walletType === w.id ? 'customer-wallet-choice-active' : ''
                }`}
                onClick={() =>
                  this.setState({
                    walletType: w.id,
                    walletQrUrl: null,
                    walletPassUrl: null,
                  })
                }
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {walletChecked && !walletQrUrl && (
          <div className="customer-info-banner customer-info-banner-yellow">
            <InfoOutlinedIcon className="customer-info-banner-icon" />
            <span>No wallet pass exists yet. Generate one below.</span>
          </div>
        )}

        {walletQrUrl && (
          <Button
            variant="outlined"
            className="customer-outlined-btn"
            onClick={this.openWalletDialog}
            startIcon={<VisibilityIcon />}
            fullWidth
          >
            View Wallet Pass
          </Button>
        )}

        <Button
          className="customer-primary-btn"
          onClick={this.generateWalletPass}
          disabled={isGeneratingWallet}
          fullWidth
        >
          {isGeneratingWallet ? 'Generating...' : 'Generate Wallet Pass'}
        </Button>

        <Dialog
          open={this.state.walletDialogOpen}
          onClose={this.closeWalletDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle style={{ textAlign: 'center' }}>
            Scan to Add Wallet Pass
          </DialogTitle>
          <DialogContent style={{ textAlign: 'center' }}>
            {walletQrUrl && (
              <img
                src={walletQrUrl}
                alt="Wallet QR"
                style={{ width: 280, height: 280, margin: '16px auto' }}
              />
            )}
            <Typography
              variant="body2"
              style={{ color: '#666', marginTop: 12 }}
            >
              This pass is device-bound and cannot be shared
            </Typography>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  renderActiveTab = () => {
    switch (this.state.activeTab) {
      case TAB_INFO:
        return this.renderInfoTab();
      case TAB_POINTS:
        return this.renderPointsTab();
      case TAB_RULES:
        return this.renderRulesTab();
      case TAB_WALLET:
        return this.renderWalletTab();
      default:
        return null;
    }
  };

  render() {
    if (!this.props.customer) {
      return (
        <React.Fragment>
          <TopToolbar
            pageName="Customer Profile"
            menuItems={[
              {
                title: 'Back',
                icon: <KeyboardBackspaceIcon />,
                onClick: () => this.props.handleBack(),
              },
            ]}
          />
          <div className="customer-profile-wrapper">
            <div className="customer-card">Customer could not be loaded.</div>
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <TopToolbar
          pageName="Customer Profile"
          menuItems={[
            {
              title: 'Back',
              icon: <KeyboardBackspaceIcon />,
              onClick: () => this.props.handleBack(),
            },
          ]}
        />

        <div className="customer-profile-wrapper">
          {this.renderHeader()}
          {this.renderTabBar()}
          <div className="customer-tab-content">{this.renderActiveTab()}</div>
        </div>

        {this.renderPhotoZoomDialog()}
      </React.Fragment>
    );
  }

  renderPhotoZoomDialog = () => {
    const c = this.props.customer || {};
    const zoomUrl =
      this.state.capturedImageURL ||
      c.capturedImageURL ||
      (c.files && c.files[0] && c.files[0].localURL) ||
      null;

    if (!zoomUrl) return null;

    return (
      <Dialog
        open={this.state.photoZoomOpen}
        onClose={this.closePhotoZoom}
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          onClick={this.closePhotoZoom}
          style={{
            padding: 0,
            backgroundColor: '#000',
            cursor: 'zoom-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={zoomUrl}
            alt="Customer"
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              display: 'block',
              margin: '0 auto',
            }}
          />
        </DialogContent>
      </Dialog>
    );
  };
}

export default CustomerProfile;
